'use client';

// ЖУРНАЛ КЛИЕНТА (владелец 25.08.2026: «ошибки должны находиться не потому, что я о них
// сообщил»). Кольцевой буфер последних событий страницы В ПАМЯТИ + зеркало в
// sessionStorage. Зеркало обязательно: половина жалоб — про ПЕРЕЗАГРУЗКУ (после выкатки
// приложение «тупит» или виснет), а перезагрузка стирает память вкладки вместе с
// причиной. Пережившие перезагрузку события возвращаются в буфер с меткой prev, первым
// событием новой загрузки идёт boot с причиной предыдущего ухода.
//
// ЧЕГО ЗДЕСЬ НЕ БЫВАЕТ: текстов сообщений, имён и телефонов людей, токенов, query-строк.
// В журнал попадают только адрес без параметров, код ответа, длительность и имя события —
// журнал уезжает к нам на сервер, и личного в нём быть не должно.
//
// Чистый модуль (без React): чтобы им пользовались и сторож (client-watch), и оболочка,
// и мост, и репортер крашей.

/** Одно событие журнала: время от загрузки страницы, метка времени, вид и детали. */
export interface JournalEvent {
  /** мс от старта загрузки страницы (performance.now, округлено). */
  t: number;
  /** абсолютное время (ISO) — единственная надёжная ось после перезагрузки. */
  at: string;
  kind: string;
  [key: string]: unknown;
}

/** Сколько событий держим. 300 — это примерно последние 3–5 минут живой работы. */
export const JOURNAL_CAP = 300;
/** Ключ зеркала. sessionStorage, а не localStorage: журнал — про ЭТУ вкладку. */
export const JOURNAL_STORE_KEY = 'scriptera:journal';
/** Не чаще раза в секунду переписываем зеркало (кроме важных событий — они пишут сразу). */
const MIRROR_MS = 1000;
/** Сколько событий кладём в снимок для репорта по умолчанию. */
export const SNAPSHOT_EVENTS = 80;
/** Потолок снимка в байтах (в context инцидента больше и не нужно). */
export const SNAPSHOT_BYTES = 6 * 1024;

// ── Чистая часть (её проверяют юниты) ────────────────────────────────────────────────

/** Добавить событие в кольцо. Иммутабельно: новый массив, старый не трогаем. */
export function ringPush<T>(buf: readonly T[], item: T, cap: number = JOURNAL_CAP): T[] {
  const next = [...buf, item];
  return next.length > cap ? next.slice(next.length - cap) : next;
}

/** Обрезать список событий СЗАДИ НАПЕРЁД (свежие важнее) под потолок байтов. */
export function trimToBytes(events: readonly JournalEvent[], maxBytes: number): JournalEvent[] {
  let out = [...events];
  while (out.length > 1 && JSON.stringify(out).length > maxBytes) {
    out = out.slice(1); // выбрасываем самое старое
  }
  return out.length === 1 && JSON.stringify(out).length > maxBytes ? [] : out;
}

/** Адрес для журнала: путь без параметров. Из query оставляем ТОЛЬКО признаки «это RSC» —
 *  по нему видно, что переход ходил за данными страницы, а не за нашей ручкой, — и «это
 *  быстрый ответ из базы» (`fast=1`, П162 п.2: его время меряется отдельно от правды). */
export function safeUrl(raw: string): { url: string; rsc?: 1; fast?: 1 } {
  // Относительный адрес разбираем от текущей страницы (вне браузера — от заглушки):
  // свой хост в журнал не пишем, чужой оставляем, чтобы отличить наш запрос от чужого.
  const base = typeof location === 'undefined' ? 'https://local' : location.href;
  try {
    const u = new URL(raw, base);
    const rsc = u.searchParams.has('_rsc') ? (1 as const) : undefined;
    const fast = u.searchParams.get('fast') === '1' ? (1 as const) : undefined;
    const own = u.host === new URL(base).host;
    return {
      url: `${own ? '' : u.host}${u.pathname}`.slice(0, 200),
      ...(rsc ? { rsc } : {}),
      ...(fast ? { fast } : {}),
    };
  } catch {
    return { url: String(raw).split('?')[0]!.slice(0, 200) };
  }
}

/**
 * КЛЮЧ ПЕРЕХОДА — путь без параметров и якоря. Одна рука на весь учёт переходов
 * (журнал и сторож): тап заводится по ССЫЛКЕ, а она бывает с параметрами
 * (`/app/messages?state=missed`), тогда как отбой приходит из usePathname() — БЕЗ них.
 * Ключи не совпадали, тап не закрывался, и через две секунды уезжал ложный доклад
 * «раздел не показан» (8 инцидентов, разбор 01.09.2026). Заодно параметрам и не место
 * в журнале — он уезжает к нам на сервер.
 */
export function navKey(raw: string): string {
  const path = String(raw).split('#')[0]!.split('?')[0]!.slice(0, 200);
  return path || '/';
}

/** Наш ли это адрес: журналим только свои запросы (чужие CDN/аналитика нас не касаются). */
export function isOwnUrl(raw: string): boolean {
  const { url } = safeUrl(raw);
  return /^\/(api|_next\/data)\//.test(url) || /^\/[a-z-]*\/?app(\/|$)/.test(url);
}

export interface WindowCap {
  windowStart: number;
  count: number;
  /** Сколько событий пропустили в текущем окне (уедут одной строкой-счётчиком). */
  dropped: number;
}

export const emptyCap = (): WindowCap => ({ windowStart: 0, count: 0, dropped: 0 });

/**
 * Кап по частоте фиксированным окном. Возвращает НОВОЕ состояние (иммутабельно):
 * allow — писать событие; flushed — сколько пропущенных надо выложить одной строкой
 * (окно кончилось). Пропущенные не теряются: их считает `dropped`.
 */
export function capAllow(
  state: WindowCap,
  now: number,
  limit: number,
  windowMs: number,
): { allow: boolean; flushed: number; state: WindowCap } {
  if (now - state.windowStart >= windowMs) {
    return {
      allow: true,
      flushed: state.dropped,
      state: { windowStart: now, count: 1, dropped: 0 },
    };
  }
  if (state.count >= limit) {
    return { allow: false, flushed: 0, state: { ...state, dropped: state.dropped + 1 } };
  }
  return { allow: true, flushed: 0, state: { ...state, count: state.count + 1 } };
}

export interface StoredJournal {
  /** Причина, по которой предыдущая загрузка кончилась (version | chunk | hydration | …). */
  reason?: string;
  events: JournalEvent[];
}

/** Разобрать зеркало. Мусор и чужая запись — пустой журнал, никаких исключений наружу. */
export function parseStored(raw: string | null | undefined): StoredJournal {
  if (!raw) return { events: [] };
  try {
    const j = JSON.parse(raw) as Partial<StoredJournal>;
    const events = Array.isArray(j?.events)
      ? j.events.filter((e): e is JournalEvent => !!e && typeof e === 'object' && typeof e.kind === 'string')
      : [];
    return { reason: typeof j?.reason === 'string' ? j.reason : undefined, events };
  } catch {
    return { events: [] };
  }
}

/** Собрать зеркало. Кап по байтам тот же, что у снимка репорта × 4 — sessionStorage тесный. */
export function serializeStored(reason: string | undefined, events: readonly JournalEvent[]): string {
  const body: StoredJournal = { ...(reason ? { reason } : {}), events: trimToBytes(events, SNAPSHOT_BYTES * 4) };
  return JSON.stringify(body);
}

/** Пометить события «из прошлой загрузки»: в снимке видно, где проходит перезагрузка. */
export function markPrev(events: readonly JournalEvent[]): JournalEvent[] {
  return events.map((e) => (e.prev ? e : { ...e, prev: 1 }));
}

// ── Живой журнал вкладки ─────────────────────────────────────────────────────────────

const browser = (): boolean => typeof window !== 'undefined';
/** ПРИЛОЖЕНИЕ: второе зеркало в localStorage (01.09.2026). В оболочке вкладка одна, а
 *  перезапуск приложения стирает sessionStorage вместе с причиной жалобы («после катки
 *  пусто, перезапустил») — из localStorage хвост поднимается и на холодном старте. */
export const JOURNAL_APP_KEY = 'scriptera:journal:app';
const inApp = (): boolean => browser() && navigator.userAgent.includes('ScripteraApp');

let buf: JournalEvent[] = [];
let started = false;
let bootReason: string | undefined;
let pendingReason: string | undefined;
let lastMirror = 0;
let mirrorTimer: number | null = null;
let fetchCap = emptyCap();
/** Последний тап по вкладке: адрес → время. Отсюда считается длительность nav.commit. */
const tapAt = new Map<string, number>();

const now = (): number =>
  typeof performance !== 'undefined' ? Math.round(performance.now()) : Date.now();

function mirror(force = false): void {
  if (!browser()) return;
  const t = Date.now();
  if (!force && t - lastMirror < MIRROR_MS) {
    if (mirrorTimer === null) {
      mirrorTimer = window.setTimeout(() => {
        mirrorTimer = null;
        mirror(true);
      }, MIRROR_MS);
    }
    return;
  }
  lastMirror = t;
  try {
    const packed = serializeStored(pendingReason, buf);
    sessionStorage.setItem(JOURNAL_STORE_KEY, packed);
    if (inApp()) localStorage.setItem(JOURNAL_APP_KEY, packed);
  } catch {
    /* приватный режим / переполненное хранилище — журнал живёт только в памяти */
  }
}

/** Виды, ради которых зеркало пишется НЕМЕДЛЕННО: после них страница может исчезнуть. */
const URGENT = new Set(['reload', 'error', 'pagehide', 'nav.slow', 'freeze']);

/** Записать событие. Никогда не бросает и ничего не рисует. */
export function log(kind: string, data?: Record<string, unknown>): void {
  if (!browser()) return;
  try {
    const ev: JournalEvent = { t: now(), at: new Date().toISOString(), kind, ...(data ?? {}) };
    buf = ringPush(buf, ev, JOURNAL_CAP);
    mirror(URGENT.has(kind));
  } catch {
    /* журнал не имеет права ронять страницу */
  }
}

/** Снимок последних событий (свежие в конце) — для репорта и отправки. */
export function snapshot(limit = SNAPSHOT_EVENTS, maxBytes = SNAPSHOT_BYTES): JournalEvent[] {
  const tail = buf.slice(Math.max(0, buf.length - limit));
  return trimToBytes(tail, maxBytes);
}

/** Весь журнал целиком (ручная отправка диагностики). */
export function all(): JournalEvent[] {
  return [...buf];
}

/** Одна строка события для СЖАТОГО снимка: «1234 fetch /api/x s=200 ms=15».
 *  Такой снимок кладётся в context инцидента — он должен читаться глазами и влезать
 *  в несколько килобайт, поэтому не JSON. */
export function compactLine(e: JournalEvent): string {
  const rest = Object.entries(e)
    .filter(([k]) => k !== 't' && k !== 'at' && k !== 'kind')
    .map(([k, v]) => (k === 'url' ? String(v) : `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`))
    .join(' ');
  return `${e.t} ${e.kind}${rest ? ' ' + rest : ''}`;
}

/** Сжатый снимок для контекста инцидента: последние `limit` событий строками, до maxBytes. */
export function compactSnapshot(limit = SNAPSHOT_EVENTS, maxBytes = SNAPSHOT_BYTES): string[] {
  const lines = buf.slice(Math.max(0, buf.length - limit)).map(compactLine);
  let out = lines;
  while (out.length > 1 && out.join('\n').length > maxBytes) out = out.slice(1);
  return out;
}

/** Причина, по которой кончилась ПРЕДЫДУЩАЯ загрузка (если она была). */
export function previousReason(): string | undefined {
  return bootReason;
}

/**
 * Записать причину предстоящей перезагрузки. Зовётся ДО window.location.reload():
 * событие + зеркало уходят синхронно, иначе причина теряется вместе с вкладкой.
 * Само поведение перезагрузок этим НЕ меняется — только журналируется.
 */
export function noteReload(reason: string, data?: Record<string, unknown>): void {
  pendingReason = reason;
  log('reload', { reason, ...(data ?? {}) });
  mirror(true);
}

/** Тап по вкладке (родной бар приложения или веб-меню) — начало отсчёта перехода.
 *  Считаем по navKey: отбой приходит без параметров, и по сырой ссылке не сошёлся бы. */
export function noteNavTap(from: string, to: string, src: 'native' | 'web'): void {
  const key = navKey(to);
  tapAt.set(key, now());
  log('nav.tap', { from: navKey(from), to: key, src });
}

/** Маршрут реально сменился. Возвращает длительность перехода (мс) или null. */
export function noteNavCommit(to: string): number | null {
  const key = navKey(to);
  const tapped = tapAt.get(key);
  tapAt.delete(key);
  const ms = tapped === undefined ? null : now() - tapped;
  log('nav.commit', { to: key, ...(ms === null ? {} : { ms }) });
  return ms;
}

/** Время тапа по адресу (сторож проверяет, дождался ли переход). */
export function tapTime(to: string): number | undefined {
  return tapAt.get(navKey(to));
}

/** Порог «долгой работы»: короче этого в журнал не пишем — он утонул бы в шуме. */
export const TASK_MS = 60;

/**
 * Замер куска работы главного потока (разбор ответа, подмена данных, перерисовка).
 * Пишется только долгое: журнал нужен, чтобы у зависания появился ВИНОВНИК, а не
 * лента обычной работы. Ничего не чинит и не меняет поведения страницы.
 */
export function noteTask(name: string, ms: number, data?: Record<string, unknown>): void {
  if (!(ms >= TASK_MS)) return;
  log('task', { n: name.slice(0, 80), ms: Math.round(ms), ...(data ?? {}) });
}

/** Событие моста: имя метода и длительность (аргументов и текстов не пишем). */
export function noteBridgeCall(method: string, ms: number, err?: string): void {
  log('bridge.call', { m: method, ms, ...(err ? { err: err.slice(0, 120) } : {}) });
}

/** Событие ОТ натива (tabSelected/layerPopped/headerAction): только имя и ключ. */
export function noteBridgeEvent(event: string, key?: string): void {
  log('bridge.event', { e: event, ...(key ? { key: String(key).slice(0, 60) } : {}) });
}

// ── Обёртка над fetch и слушатели окна ───────────────────────────────────────────────

const FETCH_LIMIT = 50;
const FETCH_WINDOW_MS = 60_000;

function logFetch(url: string, method: string, status: number | null, ms: number, err?: string): void {
  const r = capAllow(fetchCap, Date.now(), FETCH_LIMIT, FETCH_WINDOW_MS);
  fetchCap = r.state;
  if (r.flushed > 0) log('fetch.skipped', { n: r.flushed });
  if (!r.allow) return;
  const safe = safeUrl(url);
  log('fetch', {
    ...safe,
    m: method,
    ...(status === null ? {} : { s: status }),
    ms,
    ...(err ? { err: err.slice(0, 120) } : {}),
  });
}

function wrapFetch(): void {
  const original = window.fetch;
  if (typeof original !== 'function') return;
  // Одна обёртка на вкладку: помечаем оригинал, чтобы hot-reload не наслаивал вторую.
  const marked = original as typeof fetch & { __scrJournal?: boolean };
  if (marked.__scrJournal) return;
  const wrapped = async function scrJournalFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const url =
      typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
    const method =
      init?.method ?? (typeof input === 'object' && 'method' in input ? (input as Request).method : 'GET');
    if (!url || !isOwnUrl(url)) return original(input as RequestInfo, init);
    const t0 = now();
    try {
      const res = await original(input as RequestInfo, init);
      logFetch(url, method ?? 'GET', res.status, now() - t0);
      return res;
    } catch (e) {
      logFetch(url, method ?? 'GET', null, now() - t0, e instanceof Error ? e.message : String(e));
      throw e;
    }
  } as typeof fetch & { __scrJournal?: boolean };
  wrapped.__scrJournal = true;
  window.fetch = wrapped;
}

/** Признаки среды для шапки журнала (личного тут нет). */
export function journalMeta(): Record<string, unknown> {
  if (!browser()) return {};
  return {
    build: process.env.NEXT_PUBLIC_BUILD_ID ?? 'dev',
    native: document.documentElement.classList.contains('native-tabs') ? 1 : 0,
    ua: navigator.userAgent.slice(0, 200),
    path: location.pathname.slice(0, 200),
    lang: document.documentElement.lang || '',
    vw: window.innerWidth,
    vh: window.innerHeight,
  };
}

/**
 * Поднять журнал: подобрать хвост прошлой загрузки, записать boot и повесить слушатели
 * окна + обёртку fetch. Идемпотентно (зовётся из модульного кода pwa-updater).
 */
export function startJournal(): void {
  if (started || !browser()) return;
  started = true;
  try {
    let stored: StoredJournal = { events: [] };
    let restarted = false;
    try {
      stored = parseStored(sessionStorage.getItem(JOURNAL_STORE_KEY));
      // Вкладка чистая, а в localStorage приложения хвост есть — приложение перезапускали
      // (или webview убила система): причина ухода — сам перезапуск.
      if (!stored.events.length && inApp()) {
        stored = parseStored(localStorage.getItem(JOURNAL_APP_KEY));
        restarted = stored.events.length > 0;
      }
    } catch {
      /* приватный режим */
    }
    bootReason = stored.reason ?? (restarted ? 'app_restart' : undefined);
    buf = markPrev(stored.events).slice(-Math.floor(JOURNAL_CAP / 2));
    pendingReason = undefined;
    log('boot', {
      ...journalMeta(),
      ...(bootReason ? { prev: bootReason } : {}),
      ...(buf.length ? { carried: buf.length } : {}),
    });
    mirror(true);

    wrapFetch();
    document.addEventListener('visibilitychange', () => log('visibility', { s: document.visibilityState }));
    window.addEventListener('pageshow', (e) => log('pageshow', { persisted: (e as PageTransitionEvent).persisted ? 1 : 0 }));
    window.addEventListener('pagehide', (e) => log('pagehide', { persisted: (e as PageTransitionEvent).persisted ? 1 : 0 }));
    window.addEventListener('online', () => log('online'));
    window.addEventListener('offline', () => log('offline'));
  } catch {
    /* журнал — диагностика, а не функционал: страница живёт дальше */
  }
}

/** Только для тестов: чистое состояние живого журнала. */
export function resetJournalForTests(): void {
  buf = [];
  started = false;
  bootReason = undefined;
  pendingReason = undefined;
  lastMirror = 0;
  fetchCap = emptyCap();
  tapAt.clear();
}
